// Buried Treasure / Sunken Treasure burial helper. Pure. No React,
// no Three.js.
//
// ── Codex ──
//   Buried Treasure: "If cast, conjure this UNDER an allied LAND site
//     of an opponent's choice."
//   Sunken Treasure:  "If cast, conjure this UNDER an allied WATER site
//     of an opponent's choice."
//
// The cast Genesis fires an OPPONENT cell-pick constrained to the cells
// that hold one of the controller's own (allied) land / water sites.
// The runtime then conjures the Treasure into the chosen cell stamped
// SUBSURFACE so it sits buried beneath the covering site. Hidden until
// something carries it up, at which point the wired on_surface clause
// (sacrifice + draw) becomes reachable.
//
// "Under a LAND site" buries the Treasure UNDERGROUND; "under a WATER
// site" submerges it UNDERWATER. Those are exactly the subsurface flag
// values `setSubsurface` writes and the on_surface choke point watches
// for, so the buried Treasure surfaces through the same primitive any
// burrowed / submerged card uses.

/**
 * Map the covering site's element to the subsurface region the Treasure
 * is buried into.
 *
 * @param {'land'|'water'} element
 * @returns {'underground'|'underwater'}
 */
export function buryRegionForElement(element) {
  return element === 'water' ? 'underwater' : 'underground';
}

/**
 * Cells holding an ALLIED (controller's) site of the required element,
 * as "col,row" keys for a cell-pick `validCells` Set. The opponent
 * chooses one of these to bury the Treasure under.
 *
 * A site's controller is the player whose edge it faces (`rotated`):
 * p1 = not rotated, p2 = rotated. "Allied" means same controller as the
 * Treasure being cast.
 *
 * @param meshes          host.meshes Map
 * @param cellAt          (x, z) => { col, row } | null
 * @param controllerRotated  the Treasure's controller orientation (p2 = true)
 * @param isMatchingSiteAt   (col, row) => boolean. True iff the cell holds
 *        a site of the required element (land for Buried, water for Sunken)
 * @returns {Set<string>} "col,row" keys of legal burial cells
 */
export function alliedSiteCellKeys(meshes, cellAt, controllerRotated, isMatchingSiteAt) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !card.isSite) continue;
    if (card.cardId === 'sorcery-rubble') continue;
    if (!!card.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (!isMatchingSiteAt(cell.col, cell.row)) continue;
    out.add(`${cell.col},${cell.row}`);
  }
  return out;
}
